import { useEffect, useMemo, useState, useSyncExternalStore } from "react";

const MOBILE_VIEWPORT_QUERY = "(max-width: 720px)";
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
const MIN_AUTOPLAY_SECONDS = 3;
const DEFAULT_AUTOPLAY_SECONDS = 6;
const useMediaQuery = (query) => {
  const mediaQuery = useMemo(() => window.matchMedia(query), [query]);
  const subscribe = (callback) => {
    mediaQuery.addEventListener("change", callback);
    return () => mediaQuery.removeEventListener("change", callback);
  };
  return useSyncExternalStore(subscribe, () => mediaQuery.matches, () => false);
};

export const useHeroSlider = (settings) => {
  const { autoplaySeconds, fallback, slides } = useMemo(() => {
    const defaultSlide = {
      slide_id: "hero_fallback",
      label: "Mechanical services",
      desktop_image: settings?.hero_image || "/hero/pipework-desktop.webp",
      mobile_image: settings?.hero_image || "/hero/pipework-mobile.webp",
    };
    return {
      autoplaySeconds: Math.max(MIN_AUTOPLAY_SECONDS, settings?.hero_autoplay_seconds || DEFAULT_AUTOPLAY_SECONDS),
      fallback: defaultSlide,
      slides: settings?.hero_slides?.length ? settings.hero_slides : [defaultSlide],
    };
  }, [settings]);
  const [active, setActive] = useState(0);
  const mobile = useMediaQuery(MOBILE_VIEWPORT_QUERY);
  const reducedMotion = useMediaQuery(REDUCED_MOTION_QUERY);
  const autoplayEnabled = slides.length > 1 && !mobile && !reducedMotion;

  useEffect(() => {
    if (active >= slides.length) setActive(0);
  }, [active, slides, setActive]);

  useEffect(() => {
    if (mobile) setActive((current) => current === 0 ? current : 0);
  }, [mobile, setActive]);

  useEffect(() => {
    if (!autoplayEnabled) return undefined;
    const timer = window.setTimeout(() => setActive((active + 1) % slides.length), autoplaySeconds * 1000);
    return () => window.clearTimeout(timer);
  }, [active, autoplayEnabled, autoplaySeconds, setActive, slides]);

  return { active, setActive, slide: slides[active] || fallback, slides };
};